import { useState, type CSSProperties } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useSearchParams } from 'react-router-dom';
import { useApiQuery, useApiMutation } from '../use-api.js';
import { OrgCard } from '../components/OrgCard.js';
import { OptInModal } from '../components/OptInModal.js';
import type { OverrideRow } from '../components/OrgCard.js';
import type { PolicySource } from '../components/PolicySourceBadge.js';
import { ConceptHelpIcon } from '../components/ConceptHelpIcon.js';
import { AuditLogPanel, type AuditEntry } from '../components/AuditLogPanel.js';

interface EnterpriseOrg {
  login: string;
  opted_in: boolean;
  optedInBy?: string;
  optedInAt?: string;
  // A3b: persisted onto the OPTIN row by ingest-ci-artifact. Absent until
  // the first DV review fires for this org → awaiting-first-review state.
  policySourceOrg?: 'central' | 'synthesized';
  lastReviewedAt?: string;
}
interface EnterpriseOrgsResponse {
  orgs: EnterpriseOrg[];
  refreshedAt: string;
}

interface OrgConfigRow {
  org: string;
  mode: 'disabled' | 'shadow' | 'enabled';
  rerunOnPush: boolean;
  by: string;
  updatedAt: string;
}
interface RepoConfigRow extends OrgConfigRow {
  repo: string;
  // A3b: persisted onto the REPO CONFIG row by ingest-ci-artifact. Absent
  // until the first DV review fires for this repo.
  policySourceRepo?: 'central' | 'inherited' | 'synthesized';
  lastReviewedAt?: string;
}
interface DvConfigResponse {
  orgs: OrgConfigRow[];
  repos: RepoConfigRow[];
}

interface NotOptedInRowProps {
  org: string;
  onOptedIn: () => void;
}

function NotOptedInRow({ org, onOptedIn }: NotOptedInRowProps) {
  const [showModal, setShowModal] = useState(false);
  return (
    <>
      <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--loop-space-150)', padding: 'var(--loop-space-075) 0', borderBottom: '1px solid var(--loop-neutral-100)', fontSize: '0.875rem', color: 'var(--loop-neutral-900)' }}>
        <span style={{ flex: 1 }}>{org}</span>
        <button
          className="btn-primary btn-sm"
          onClick={() => setShowModal(true)}
        >
          Opt in
        </button>
      </div>
      {showModal && (
        <OptInModal
          org={org}
          onClose={() => setShowModal(false)}
          onSaved={() => { setShowModal(false); onOptedIn(); }}
        />
      )}
    </>
  );
}

function yesterdayUtc(): string {
  const d = new Date();
  d.setUTCDate(d.getUTCDate() - 1);
  return d.toISOString().slice(0, 10);
}

// Operator tool — sync-invokes the aggregate-daily Lambda for a specific UTC
// date. Same Lambda EventBridge fires at 00:30 UTC daily; this surfaces the
// day-override on the handler so the scorecard tiles can reflect today's data
// without waiting for tomorrow's natural tick.
function BackfillRollupsPanel() {
  const [day, setDay] = useState<string>(yesterdayUtc());
  const backfill = useApiMutation('/api/ops/aggregate-daily');

  // The mutation hook resolves with the Lambda's JSON. Narrow it here.
  const data = backfill.data as { ok?: boolean; day?: string; rollups_written?: number } | undefined;
  const errMessage = backfill.error instanceof Error ? backfill.error.message : null;

  return (
    <div style={{ marginTop: 'var(--loop-space-300)', borderTop: '1px solid var(--loop-neutral-300)', paddingTop: 'var(--loop-space-200)' }}>
      <div style={{ fontSize: '0.875rem', fontWeight: 528, color: 'var(--loop-neutral-700)', marginBottom: 'var(--loop-space-100)', paddingBottom: 'var(--loop-space-075)', borderBottom: '1px solid var(--loop-neutral-300)' }}>
        Backfill daily rollups
      </div>
      <p style={{ fontSize: '0.8rem', color: 'var(--loop-neutral-700)', margin: '0 0 var(--loop-space-150) 0' }}>
        Re-run the daily aggregator for a specific UTC date. Use this to populate today&apos;s scorecard tiles
        instead of waiting for the 00:30 UTC cron, or to rebuild a historical day after a data correction.
      </p>
      <div style={{ display: 'flex', gap: 'var(--loop-space-100)', alignItems: 'center' }}>
        <input
          type="date"
          value={day}
          onChange={(e) => setDay(e.target.value)}
          disabled={backfill.isPending}
          style={{
            padding: 'var(--loop-space-100) var(--loop-space-150)',
            border: '1px solid var(--loop-neutral-300)',
            borderRadius: 'var(--loop-radius-sm)',
            fontSize: '0.875rem',
          }}
          aria-label="UTC date to backfill"
        />
        <button
          type="button"
          onClick={() => backfill.mutate({ day })}
          disabled={backfill.isPending || !day}
          style={{
            padding: 'var(--loop-space-100) var(--loop-space-200)',
            border: '1px solid var(--loop-brand-600)',
            background: 'var(--loop-brand-600)',
            color: 'white',
            borderRadius: 'var(--loop-radius-sm)',
            fontSize: '0.875rem',
            fontWeight: 528,
            cursor: backfill.isPending ? 'not-allowed' : 'pointer',
          }}
        >
          {backfill.isPending ? 'Running…' : 'Run backfill'}
        </button>
      </div>
      {data?.ok && (
        <div style={{ marginTop: 'var(--loop-space-100)', fontSize: '0.8rem', color: 'var(--loop-green-700)' }}>
          Wrote {data.rollups_written} rollup{data.rollups_written === 1 ? '' : 's'} for {data.day}.
        </div>
      )}
      {errMessage && (
        <div style={{ marginTop: 'var(--loop-space-100)', fontSize: '0.8rem', color: 'var(--loop-red-700)' }}>
          {errMessage}
        </div>
      )}
    </div>
  );
}

const wrap: CSSProperties = { padding: 'var(--loop-space-300)' };
const heading: CSSProperties = {
  fontSize: '1.25rem',
  fontWeight: 664,
  color: 'var(--loop-neutral-1100)',
  marginBottom: 'var(--loop-space-200)',
};

/**
 * Resolve the org-level policy-source badge from the OPTIN row data.
 * - `customized` ← runner has reported `policySourceOrg: 'central'`
 * - `synthesized` ← runner has reported `policySourceOrg: 'synthesized'`
 *   (no central.yml entry; the badge prompts a policy audit)
 * - `awaiting-first-review` ← opted-in but DV hasn't run yet (field absent)
 */
function orgBadgeSource(o: EnterpriseOrg): PolicySource {
  if (o.policySourceOrg === 'synthesized') return 'synthesized';
  if (o.policySourceOrg === 'central') return 'customized';
  return 'awaiting-first-review';
}

/**
 * Resolve the per-repo override badge. Repo-level is more nuanced than org-
 * level: a repo can `inherit` the org default (no central.yml block of its
 * own) while the org itself is `customized`.
 */
function repoBadgeSource(r: RepoConfigRow): PolicySource | undefined {
  if (!r.policySourceRepo) return undefined; // awaiting-first-review at the repo
  if (r.policySourceRepo === 'central') return 'customized';
  if (r.policySourceRepo === 'inherited') return 'inherited';
  return 'synthesized';
}

export function DvConfig() {
  const qc = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();
  const enterprise = useApiQuery<EnterpriseOrgsResponse>(['enterprise-orgs'], '/api/enterprise-orgs');
  const config = useApiQuery<DvConfigResponse>(['dv-config'], '/api/dv-config');
  const audit = useApiQuery<AuditEntry[]>(['audit-global'], '/api/audit?global=true&limit=50');

  // A3b: filter the org list to just synthesized rows when ?auditOnly=1.
  // The filter is the "show orgs needing policy audit" toggle — landing
  // page UX for the operator who wants to triage which opted-in orgs are
  // running on synthesized defaults rather than reviewed-and-customized
  // central.yml entries.
  const auditOnly = searchParams.get('auditOnly') === '1';
  function toggleAuditFilter() {
    const next = new URLSearchParams(searchParams);
    if (auditOnly) next.delete('auditOnly');
    else next.set('auditOnly', '1');
    setSearchParams(next);
  }

  if (enterprise.isLoading || config.isLoading) {
    return <div style={wrap}>Loading…</div>;
  }
  if (enterprise.error) {
    return <div style={wrap}>Error loading orgs: {(enterprise.error as Error).message}</div>;
  }
  if (config.error) {
    return <div style={wrap}>Error loading DV config: {(config.error as Error).message}</div>;
  }

  function onMutated() {
    void qc.invalidateQueries({ queryKey: ['enterprise-orgs'] });
    void qc.invalidateQueries({ queryKey: ['dv-config'] });
    void qc.invalidateQueries({ queryKey: ['audit-global'] });
  }

  const allOptedIn = enterprise.data!.orgs.filter((o) => o.opted_in);
  const notOptedIn = enterprise.data!.orgs.filter((o) => !o.opted_in);

  // Apply the audit filter: only show rows the operator likely wants to act on.
  const optedIn = auditOnly
    ? allOptedIn.filter((o) => orgBadgeSource(o) === 'synthesized')
    : allOptedIn;
  const synthesizedCount = allOptedIn.filter((o) => orgBadgeSource(o) === 'synthesized').length;

  const overridesByOrg = new Map<string, OverrideRow[]>();
  for (const r of config.data!.repos) {
    const list = overridesByOrg.get(r.org) ?? [];
    const repoSource = repoBadgeSource(r);
    list.push(
      repoSource
        ? { ...r, policySource: repoSource, lastReviewedAt: r.lastReviewedAt }
        : r
    );
    overridesByOrg.set(r.org, list);
  }
  const orgDefaults = new Map(config.data!.orgs.map((o) => [o.org, o] as const));

  return (
    <div style={wrap}>
      <h2 style={{ ...heading, display: 'flex', alignItems: 'center', gap: 'var(--loop-space-150)' }}>
        <img src="/darth-vader.gif" alt="" style={{ width: 32, height: 32 }} />
        Diff Vader Configuration
        <ConceptHelpIcon slug="dv-config" />
      </h2>

      <div
        style={{
          background: 'var(--loop-brand-100)',
          border: '1px solid var(--loop-brand-500)',
          borderLeft: '4px solid var(--loop-brand-600)',
          borderRadius: 'var(--loop-radius-sm)',
          padding: 'var(--loop-space-200) var(--loop-space-250)',
          marginBottom: 'var(--loop-space-300)',
          fontSize: '0.875rem',
          color: 'var(--loop-neutral-1100)',
          lineHeight: 1.5,
        }}
      >
        <p style={{ margin: 0 }}>
          This page controls <strong>where Diff Vader runs</strong> — opt-in per
          organization, mode (disabled / shadow / enabled), and whether reviews
          re-fire on every push.
        </p>
        <p style={{ margin: 'var(--loop-space-100) 0 0 0' }}>
          The <strong>review policy itself</strong> — which reviewers run, what
          each one looks at, severity gates — lives in{' '}
          <a
            href="https://github.com/rewind-corp/diff-vader/blob/main/policies/central.yml"
            target="_blank"
            rel="noopener noreferrer"
            style={{ color: 'var(--loop-brand-600)', fontFamily: 'monospace' }}
          >
            rewind-corp/diff-vader:policies/central.yml
          </a>
          . Edit it via PR in that repo.
        </p>
      </div>

      {/* A3b: audit filter — surfaces orgs running on synthesized defaults so
          the operator can triage them. Only shown when at least one such org
          exists (no point in offering a filter that returns nothing).
          ConceptHelpIcon links to the 'policy-source' help concept. */}
      {(synthesizedCount > 0 || auditOnly) && (
        <div
          style={{
            display: 'flex', alignItems: 'center', gap: 'var(--loop-space-150)',
            marginBottom: 'var(--loop-space-200)',
            fontSize: '0.875rem', color: 'var(--loop-neutral-700)',
          }}
        >
          <button
            type="button"
            onClick={toggleAuditFilter}
            style={{
              padding: '4px 12px',
              border: '1px solid #f59e0b',
              background: auditOnly ? '#f59e0b' : 'white',
              color: auditOnly ? 'white' : '#92400e',
              borderRadius: 'var(--loop-radius-sm)',
              fontSize: '0.8125rem', fontWeight: 528, cursor: 'pointer',
            }}
          >
            {auditOnly
              ? `✓ Showing synthesized only (${synthesizedCount})`
              : `Show orgs needing policy audit (${synthesizedCount})`}
          </button>
          <ConceptHelpIcon slug="policy-source" />
        </div>
      )}

      {optedIn.map((o) => {
        const defRow = orgDefaults.get(o.login);
        return (
          <OrgCard
            key={o.login}
            org={o.login}
            optedIn={true}
            defaultConfig={defRow ? { mode: defRow.mode, rerunOnPush: defRow.rerunOnPush } : null}
            overrides={overridesByOrg.get(o.login) ?? []}
            onMutated={onMutated}
            policySource={orgBadgeSource(o)}
            lastReviewedAt={o.lastReviewedAt}
          />
        );
      })}
      {auditOnly && optedIn.length === 0 && (
        <div style={{ fontSize: '0.875rem', color: 'var(--loop-neutral-600)', padding: 'var(--loop-space-200) 0' }}>
          No orgs are currently running on synthesized defaults. 🎉
        </div>
      )}

      <div style={{ marginTop: 'var(--loop-space-200)' }}>
        <div style={{ fontSize: '0.875rem', fontWeight: 528, color: 'var(--loop-neutral-700)', marginBottom: 'var(--loop-space-100)', paddingBottom: 'var(--loop-space-075)', borderBottom: '1px solid var(--loop-neutral-300)' }}>
          Other Rewind orgs (DV not enabled here)
        </div>
        {notOptedIn.length === 0 ? (
          <p style={{ color: 'var(--loop-neutral-700)', fontSize: '0.875rem' }}>
            All Rewind orgs are opted in.
          </p>
        ) : (
          notOptedIn.map((o) => (
            <NotOptedInRow key={o.login} org={o.login} onOptedIn={onMutated} />
          ))
        )}
      </div>

      {allOptedIn.length === 0 && notOptedIn.length === 0 && (
        <div style={{ fontSize: '0.875rem', color: 'var(--loop-neutral-600)', padding: 'var(--loop-space-200) 0' }}>
          No enterprise orgs found.
        </div>
      )}

      <BackfillRollupsPanel />

      <AuditLogPanel
        events={audit.data ?? []}
        isLoading={audit.isLoading}
        error={audit.error}
      />
    </div>
  );
}
